import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, MessageCircle, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
} from '@/ui/Panel';
import { Callout } from '@/ui/Callout';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, SearchInput, Textarea } from '@/ui/Field';
import {
  ReceiptLines,
  ReceiptTenders,
  ReceiptTotals,
  TxnStatusBadge,
} from '@/ui/Receipt';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { ApiError, api, errorMessage, saveBlob } from '@/lib/api/client';
import { isUuid, mapTransactionDetail } from '@/lib/api/mappers';
import { useAtLeast } from '@/lib/auth';
import { dateTime, money, num, phone as fmtPhone, relativeTime } from '@/lib/format';
import type { PaginatedResponse, TransactionReassignmentEntry } from '@/lib/api/types';

/**
 * The full receipt.
 *
 * Everything on this screen came through `mapTransactionDetail` — this is the
 * one endpoint that hands back raw `transaction_items` / `transaction_tenders`
 * rows, and rendering those unmapped produces a confidently blank receipt.
 *
 * The dangerous affordance here is **reassign customer**: it does not just
 * repoint a foreign key, it reverses the house-account ledger entry on the old
 * customer and re-charges the new one, moving two balances. It is manager-only
 * on the server, confirmed here, and never optimistic.
 */

export const Route = createFileRoute('/_app/transactions_/$id')({
  staticData: { crumb: 'Receipt' },
  component: ReceiptScreen,
});

/**
 * The list screen validates a fully-populated search object, so linking back to
 * it has to name the defaults. Going "back" lands on today's receipts, which is
 * where an operator wants to be far more often than on a stale filter set.
 */
const TRANSACTIONS_LIST_SEARCH = {
  range: 'today',
  status: 'all',
  q: '',
  page: 1,
} as const;

/** Customer rows come back with the FoxPro name order already applied. */
function snapshotName(snap?: {
  firstName: string;
  lastName: string;
  accountNumber: string | null;
}): string {
  if (!snap) return 'Unknown';
  return [snap.lastName, snap.firstName].filter(Boolean).join(' ') || 'Unnamed';
}

function ReceiptScreen() {
  const { id } = Route.useParams();
  const valid = isUuid(id);

  const detail = useQuery({
    queryKey: ['transaction', id],
    queryFn: async () =>
      mapTransactionDetail(await api.get<unknown>(`/transactions/${id}`)),
    enabled: valid,
  });

  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const isManager = useAtLeast('manager');

  const downloadPdf = useMutation({
    mutationFn: async () => api.download(`/transactions/${id}/receipt.pdf`),
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename || `receipt-${id.slice(0, 8).toUpperCase()}.pdf`);
    },
    onError: (err) =>
      toast.error('Could not build the receipt', {
        description: errorMessage(err),
      }),
  });

  if (!valid) {
    return (
      <Page>
        <PageHeader eyebrow="Selling" title="Receipt" />
        <PageBody>
          <EmptyState
            title="That is not a receipt id"
            description={`“${id}” is not a valid receipt id, so there is nothing to load.`}
            action={
              <Button size="sm" asChild>
                <Link to="/transactions" search={TRANSACTIONS_LIST_SEARCH}>
                  Back to transactions
                </Link>
              </Button>
            }
          />
        </PageBody>
      </Page>
    );
  }

  const txn = detail.data;
  const houseTender = (txn?.tenders ?? []).reduce(
    (sum, t) => (t.type === 'house' ? sum + t.amount : sum),
    0
  );
  const currentCustomerId = txn?.customerId == null ? null : Number(txn.customerId);

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono">{`Receipt ${id.slice(0, 8).toUpperCase()}`}</span>
            {txn ? <TxnStatusBadge status={txn.status} /> : null}
          </span>
        }
        subtitle={txn ? dateTime(txn.occurredAt) : undefined}
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              asChild
            >
              <Link to="/transactions" search={TRANSACTIONS_LIST_SEARCH}>
                Transactions
              </Link>
            </Button>
            {txn ? (
              <>
                <Button
                  size="sm"
                  icon={<Download className="size-3.5" />}
                  loading={downloadPdf.isPending}
                  onClick={() => downloadPdf.mutate()}
                >
                  Receipt PDF
                </Button>
                <Button
                  size="sm"
                  icon={<MessageCircle className="size-3.5" />}
                  onClick={() => setWhatsappOpen(true)}
                >
                  WhatsApp
                </Button>
                {isManager && currentCustomerId !== null ? (
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<UserCog className="size-3.5" />}
                    onClick={() => setReassignOpen(true)}
                  >
                    Reassign customer
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      <PageBody>
        {detail.isLoading ? (
          <div className="mx-auto grid max-w-5xl gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <Skeleton className="h-80 w-full" />
            <Skeleton className="h-60 w-full" />
          </div>
        ) : detail.error || !txn ? (
          <ErrorState
            title="Could not load this receipt"
            message={errorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        ) : (
          <div className="mx-auto grid max-w-5xl gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="space-y-3">
              {txn.overReturnFlags.length > 0 ? (
                <Callout tone="warn" title="Returned for more than it sold">
                  {num(txn.overReturnFlags.length)} sync{' '}
                  {txn.overReturnFlags.length === 1 ? 'event has' : 'events have'} pushed
                  returns against this receipt past the quantity it originally sold.
                  Reconcile before trusting the stock movement it produced.
                </Callout>
              ) : null}

              <Panel
                title="Lines"
                description={`${num(txn.items.length)} recorded`}
                bodyClassName="p-3 pt-1"
              >
                <ReceiptLines items={txn.items} />
              </Panel>

              <Panel title="Tenders" bodyClassName="p-3 pt-1">
                <ReceiptTenders tenders={txn.tenders} />
                <div className="border-line mt-2 border-t pt-2">
                  <ReceiptTotals
                    subtotal={txn.subtotal}
                    discountTotal={txn.discountTotal}
                    taxTotal={txn.taxTotal}
                    total={txn.total}
                    tenders={txn.tenders}
                  />
                </div>
              </Panel>

              <ReassignmentHistory entries={txn.reassignmentHistory ?? []} />
            </div>

            <div className="space-y-3">
              <Panel title="Attribution">
                <DetailList>
                  <Detail label="Cashier">{txn.cashierName ?? '—'}</Detail>
                  <Detail label="Register">{txn.registerName ?? '—'}</Detail>
                  <Detail label="Customer">
                    {currentCustomerId === null
                      ? 'Walk-in'
                      : txn.customerCompanyName || txn.customerName || 'Customer'}
                  </Detail>
                  <Detail label="Customer phone">
                    {txn.customerPhone ? fmtPhone(txn.customerPhone) : '—'}
                  </Detail>
                  <Detail label="Shift" mono>
                    {txn.shiftId ?? '—'}
                  </Detail>
                </DetailList>
              </Panel>

              <Panel title="Provenance">
                <DetailList>
                  <Detail label="Receipt id" mono>
                    <Code copyable>{txn.id}</Code>
                  </Detail>
                  <Detail label="Occurred">{dateTime(txn.occurredAt)}</Detail>
                  <Detail label="Synced">
                    {txn.syncedAt
                      ? `${dateTime(txn.syncedAt)} · ${relativeTime(txn.syncedAt)}`
                      : '—'}
                  </Detail>
                  {txn.legacyTick || txn.legacyGuid ? (
                    <Detail label="Legacy record" mono>
                      {txn.legacyTick ?? txn.legacyGuid}
                    </Detail>
                  ) : null}
                  {txn.originalTransactionId ? (
                    <Detail label="Returns against">
                      <Link
                        to="/transactions/$id"
                        params={{ id: txn.originalTransactionId }}
                        className="text-signal-ink font-mono hover:underline"
                      >
                        {txn.originalTransactionId.slice(0, 8).toUpperCase()}
                      </Link>
                    </Detail>
                  ) : null}
                  {txn.voidOfTransactionId ? (
                    <Detail label="Voids">
                      <Link
                        to="/transactions/$id"
                        params={{ id: txn.voidOfTransactionId }}
                        className="text-signal-ink font-mono hover:underline"
                      >
                        {txn.voidOfTransactionId.slice(0, 8).toUpperCase()}
                      </Link>
                    </Detail>
                  ) : null}
                </DetailList>
              </Panel>
            </div>
          </div>
        )}
      </PageBody>

      {txn ? (
        <WhatsAppDialog
          open={whatsappOpen}
          onOpenChange={setWhatsappOpen}
          transactionId={txn.id}
          defaultPhone={txn.customerPhone ?? ''}
        />
      ) : null}

      {txn && currentCustomerId !== null ? (
        <ReassignDialog
          open={reassignOpen}
          onOpenChange={setReassignOpen}
          transactionId={txn.id}
          currentCustomerId={currentCustomerId}
          currentCustomerName={txn.customerCompanyName || txn.customerName || 'Customer'}
          houseTenderCents={houseTender}
        />
      ) : null}
    </Page>
  );
}

/* ── Reassignment audit trail ─────────────────────────────────────────────── */

function ReassignmentHistory({ entries }: { entries: TransactionReassignmentEntry[] }) {
  if (entries.length === 0) return null;

  return (
    <Panel
      title="Customer reassignments"
      description="Every move of this receipt between accounts, newest first"
    >
      <ol className="space-y-2.5">
        {entries.map((entry) => {
          const d = entry.details;
          const actor =
            [entry.actorFirstName, entry.actorLastName].filter(Boolean).join(' ') ||
            (entry.actorUserId === null ? 'Unknown' : `User ${entry.actorUserId}`);
          return (
            <li
              key={entry.id}
              className="border-line bg-surface-sunken rounded border p-2.5"
            >
              <div className="text-ink flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">{snapshotName(d.oldCustomerSnapshot)}</span>
                <span className="text-ink-faint">→</span>
                <span className="font-medium">{snapshotName(d.newCustomerSnapshot)}</span>
                {d.hadHouseTender ? (
                  <Badge tone="warn">Ledger moved</Badge>
                ) : (
                  <Badge tone="neutral">Pointer only</Badge>
                )}
              </div>
              <div className="text-2xs text-ink-faint mt-1">
                {actor}
                {entry.actorRole ? ` · ${entry.actorRole}` : ''} ·{' '}
                {dateTime(entry.createdAt)}
              </div>
              {d.hadHouseTender ? (
                <div className="text-2xs text-ink-muted mt-1.5 font-mono tabular-nums">
                  {money(d.netLedgerCents)} reversed from{' '}
                  {snapshotName(d.oldCustomerSnapshot)} (
                  {money(d.oldCustomerBalanceBefore)} → {money(d.oldCustomerBalanceAfter)}
                  ) and charged to {snapshotName(d.newCustomerSnapshot)} (
                  {money(d.newCustomerBalanceBefore)} → {money(d.newCustomerBalanceAfter)}
                  )
                </div>
              ) : null}
              <div className="text-ink-muted mt-1.5 text-xs">“{d.reason}”</div>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

/* ── WhatsApp ─────────────────────────────────────────────────────────────── */

function WhatsAppDialog({
  open,
  onOpenChange,
  transactionId,
  defaultPhone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string;
  defaultPhone: string;
}) {
  const [phone, setPhone] = useState(defaultPhone);
  const [message, setMessage] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean; phone: string }>(
        `/transactions/${transactionId}/receipt/whatsapp`,
        {
          phone: phone.trim() || undefined,
          message: message.trim() || undefined,
        }
      ),
    onSuccess: (result) => {
      toast.success('Receipt sent', {
        description: `Delivered to ${fmtPhone(result.phone)}.`,
      });
      onOpenChange(false);
      setRefusal(null);
    },
    onError: (err) => {
      // 422 is the honest business refusal: WhatsApp is not linked, or there is
      // no number to send to. Show it in place instead of a toast that vanishes.
      const message422 = err instanceof ApiError && err.status === 422;
      setRefusal(errorMessage(err));
      if (!message422) {
        toast.error('Could not send the receipt', { description: errorMessage(err) });
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setRefusal(null);
        onOpenChange(next);
      }}
      title="Send this receipt on WhatsApp"
      description="Sends the same PDF the printer produces, as a document."
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={send.isPending}
            disabled={!phone.trim()}
            onClick={() => {
              setRefusal(null);
              send.mutate();
            }}
          >
            Send
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {refusal ? (
          <Callout tone="negative" title="The server refused this send">
            {refusal}
          </Callout>
        ) : null}
        <Field
          label="Phone"
          hint={
            defaultPhone
              ? 'Prefilled from the customer on this receipt.'
              : 'No customer phone on file — type the number to send to.'
          }
        >
          <Input
            size="sm"
            mono
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
            placeholder="555 123 4567"
          />
        </Field>
        <Field label="Message" hint="Optional. Replaces the default caption.">
          <Textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            placeholder="Your receipt from Kingston Kosher"
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ── Reassign customer ────────────────────────────────────────────────────── */

interface CustomerPick {
  id: number | string;
  name: string;
  accountNumber?: string | null;
  companyName?: string | null;
  balance: number;
  hasCredit?: boolean | null;
}

interface ReassignResult {
  transactionId: string;
  oldCustomerId: number;
  newCustomerId: number;
  hadHouseTender: boolean;
  netLedgerCents: number;
  oldCustomerBalanceBefore: number;
  oldCustomerBalanceAfter: number;
  newCustomerBalanceBefore: number;
  newCustomerBalanceAfter: number;
}

function ReassignDialog({
  open,
  onOpenChange,
  transactionId,
  currentCustomerId,
  currentCustomerName,
  houseTenderCents,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string;
  currentCustomerId: number;
  currentCustomerName: string;
  houseTenderCents: number;
}) {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [rawQuery, setRawQuery] = useState('');
  const debounced = useDebouncedValue(rawQuery);
  const [target, setTarget] = useState<CustomerPick | null>(null);
  const [reason, setReason] = useState('');

  const results = useQuery({
    queryKey: ['customers', 'reassign-search', debounced],
    queryFn: () =>
      api.get<PaginatedResponse<CustomerPick>>('/customers', {
        params: { search: debounced, pageSize: 8, sort: 'name', order: 'asc' },
      }),
    enabled: open && debounced.trim().length >= 2,
  });

  function close() {
    onOpenChange(false);
    setStage('form');
    setRawQuery('');
    setTarget(null);
    setReason('');
  }

  const reassign = useMutation({
    mutationFn: (body: { customerId: number; reason: string }) =>
      api.patch<ReassignResult>(`/transactions/${transactionId}/customer`, body),
    onSuccess: (result) => {
      toast.success('Receipt reassigned', {
        description: result.hadHouseTender
          ? `${money(result.netLedgerCents)} moved between house accounts. New balance ${money(result.newCustomerBalanceAfter)}.`
          : 'No house tender on this receipt — the customer pointer moved, no ledger entries were written.',
      });
      // No optimistic write anywhere on this path: the server is the only thing
      // that knows the resulting balances, so everything it touched is refetched.
      void queryClient.invalidateQueries({ queryKey: ['transaction', transactionId] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      close();
    },
    onError: (err) => {
      toast.error('Could not reassign this receipt', {
        description: errorMessage(err),
      });
      setStage('form');
    },
  });

  const trimmedReason = reason.trim();
  const sameCustomer = target !== null && Number(target.id) === currentCustomerId;
  const targetId = target === null ? null : Number(target.id);
  const validTarget =
    targetId !== null && Number.isInteger(targetId) && targetId > 0 && !sameCustomer;
  const ready = validTarget && trimmedReason.length >= 1 && trimmedReason.length <= 500;

  const consequences = [
    `Repoints receipt ${transactionId.slice(0, 8).toUpperCase()} from ${currentCustomerName} to ${target?.name ?? '—'}.`,
    houseTenderCents > 0
      ? `Reverses the ${money(houseTenderCents)} house charge off ${currentCustomerName}'s account and posts it to ${target?.name ?? 'the new account'} — the server reverses the net across any linked refunds or voids, which may differ from this receipt alone.`
      : 'This receipt has no house tender, so no ledger entries are written — only the customer pointer moves.',
    'Both balances change the moment this is confirmed. There is no undo.',
  ];

  return (
    <>
      <Dialog
        open={open && stage === 'form'}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Reassign this receipt to another customer"
        description="Manager-only correction for a sale rung up against the wrong account."
        width="md"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!ready}
              onClick={() => setStage('confirm')}
            >
              Review consequences
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Callout tone="warn" title="This moves money, not just a label">
            {houseTenderCents > 0
              ? `This receipt carries a ${money(houseTenderCents)} house tender. Reassigning it reverses that charge on ${currentCustomerName} and re-charges the new account.`
              : 'This receipt has no house tender, so only the customer pointer moves. If stray ledger rows exist the server will refuse rather than leave balances wrong.'}
          </Callout>

          <Field
            label="Currently assigned to"
            hint="Walk-in receipts cannot be reassigned from here."
          >
            <div className="border-line bg-surface-sunken text-ink rounded border px-2.5 py-1.5 text-sm">
              {currentCustomerName}
            </div>
          </Field>

          <Field
            label="Move to"
            required
            error={sameCustomer ? 'That is the account it is already on.' : null}
            hint="Search by name, account number, phone or company."
          >
            <SearchInput
              size="sm"
              value={rawQuery}
              onValueChange={setRawQuery}
              placeholder="Type at least two characters…"
            />
          </Field>

          {debounced.trim().length >= 2 ? (
            <div className="border-line max-h-52 overflow-y-auto rounded border">
              {results.isLoading ? (
                <div className="p-2">
                  <Skeleton className="h-6 w-full" />
                </div>
              ) : results.error ? (
                <p className="text-negative p-2 text-xs">{errorMessage(results.error)}</p>
              ) : (results.data?.data ?? []).length === 0 ? (
                <p className="text-ink-faint p-2 text-xs">No customer matches that.</p>
              ) : (
                <ul className="divide-line-soft divide-y">
                  {(results.data?.data ?? []).map((c) => {
                    const isCurrent = Number(c.id) === currentCustomerId;
                    const isPicked =
                      target !== null && Number(target.id) === Number(c.id);
                    return (
                      <li key={String(c.id)}>
                        <button
                          type="button"
                          disabled={isCurrent}
                          onClick={() => setTarget(c)}
                          className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs ${
                            isPicked ? 'bg-signal-muted' : 'hover:bg-surface-hover'
                          } ${isCurrent ? 'cursor-not-allowed opacity-40' : ''}`}
                        >
                          <span className="text-ink min-w-0 flex-1 truncate">
                            {c.companyName || c.name}
                          </span>
                          {c.accountNumber ? <Code>{c.accountNumber}</Code> : null}
                          {isCurrent ? <Badge tone="neutral">Current</Badge> : null}
                          {c.hasCredit === false ? (
                            <Badge tone="warn">No credit</Badge>
                          ) : null}
                          <span className="text-ink-muted shrink-0 font-mono tabular-nums">
                            {money(c.balance)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}

          {target && target.hasCredit === false ? (
            <Callout tone="warn" title="That account is not set up for credit">
              The server allows the correction anyway — a manager doing this is stating a
              fact about who took the goods — but the balance will go onto an account with
              no credit flag.
            </Callout>
          ) : null}

          <Field
            label="Reason"
            required
            hint={`${trimmedReason.length}/500 · recorded in the audit log`}
            error={
              trimmedReason.length > 500 ? 'Too long — 500 characters maximum.' : null
            }
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="Cashier selected the wrong account at the register"
            />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && stage === 'confirm'}
        onOpenChange={(next) => {
          if (!next) setStage('form');
        }}
        title="Reassign and move the ledger"
        description="The server does this in one financial transaction. Nothing here is optimistic."
        confirmLabel="Reassign receipt"
        typeToConfirm="REASSIGN"
        consequences={consequences}
        loading={reassign.isPending}
        onConfirm={() => {
          if (targetId === null) return;
          reassign.mutate({ customerId: targetId, reason: trimmedReason });
        }}
      />
    </>
  );
}
